import { db } from '@/firebase';
import { collection, query, where, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp } from 'lucide-react';
import { format, startOfMonth } from 'date-fns';
import { useEffect, useState } from 'react';
import type { BudgetCategory, MonthlyBudget } from '@/types/finance';

interface FinanceWidgetProps {
  householdId: string | null;
}

interface Transaction {
  amount: number;
  type: string;
  category?: string;
  date: string;
}

const CATEGORY_COLORS = ['bg-brand', 'bg-emerald-500', 'bg-amber-500'];

export default function FinanceWidget({ householdId }: FinanceWidgetProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalSpend, setTotalSpend] = useState<number>(0);
  const [topCategories, setTopCategories] = useState<{ category: string; amount: number }[]>([]);
  const [budget, setBudget] = useState<MonthlyBudget | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const currentMonth = format(new Date(), 'MMMM');
  const yearMonth = format(startOfMonth(new Date()), 'yyyy-MM');
  const firstDayOfMonthStr = format(startOfMonth(new Date()), 'yyyy-MM-dd');

  useEffect(() => {
    if (!householdId) { setLoading(false); return; }

    // Fetch budget for current month
    getDoc(doc(db, 'households', householdId, 'budgets', yearMonth))
      .then(snap => { if (snap.exists()) setBudget(snap.data() as MonthlyBudget); })
      .catch(() => {});

    // Subscribe to transactions
    const q = query(
      collection(db, 'households', householdId, 'transactions'),
      where('date', '>=', firstDayOfMonthStr),
      orderBy('date', 'desc'),
      limit(100)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loaded: Transaction[] = [];
      snapshot.forEach(d => loaded.push(d.data() as Transaction));
      const spend = loaded.filter(t => t.type === 'expense').reduce((s, t) => s + (t.amount || 0), 0);
      setTotalSpend(spend);
      const catTotals: Record<string, number> = {};
      loaded.filter(t => t.type === 'expense' && t.category).forEach(t => {
        catTotals[t.category!] = (catTotals[t.category!] || 0) + t.amount;
      });
      setTopCategories(
        Object.entries(catTotals).map(([category, amount]) => ({ category, amount }))
          .sort((a, b) => b.amount - a.amount).slice(0, 3)
      );
      setTransactions(loaded);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsubscribe();
  }, [householdId, firstDayOfMonthStr, yearMonth]);

  if (!householdId) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 flex flex-col items-center justify-center min-h-[200px]">
        <DollarSign className="w-10 h-10 text-slate-400 mb-3" />
        <p className="text-sm text-slate-500 dark:text-slate-400">Link a household to track finances</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-6 h-6 text-slate-400" />
          <div><p className="font-semibold text-slate-800 dark:text-slate-100">Finance Overview</p><p className="text-sm text-slate-500">{currentMonth}</p></div>
        </div>
        <div className="space-y-3">{[1,2,3].map(i => (<div key={i} className="h-4 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />))}</div>
      </div>
    );
  }

  // Budget progress view
  const hasBudget = budget && budget.categories && Object.keys(budget.categories).length > 0;

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-slate-700 dark:text-slate-200" />
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100">Finance Overview</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{currentMonth}</p>
          </div>
        </div>
        <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
          ${totalSpend.toLocaleString('en-US', { minimumFractionDigits: 0 })}
        </div>
      </div>

      {hasBudget ? (
        <div className="space-y-2 mt-4">
          {Object.entries(budget!.categories)
            .filter(([, cat]: [string, BudgetCategory]) => cat.ceiling > 0)
            .slice(0, 4)
            .map(([catId, cat]: [string, BudgetCategory]) => {
              const spent = topCategories.find(c => c.category === catId)?.amount ?? 0;
              const pct = Math.min((spent / cat.ceiling) * 100, 100);
              const color = pct < 80 ? 'bg-emerald-500' : pct < 100 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <div key={catId}>
                  <div className="flex justify-between text-xs text-slate-600 dark:text-slate-400 mb-1">
                    <span className="capitalize">{catId}</span>
                    <span>${spent.toFixed(0)} / ${cat.ceiling}</span>
                  </div>
                  <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {topCategories.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No expenses this month</p>
          ) : (
            topCategories.map((cat, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-20 text-sm text-slate-700 dark:text-slate-300 truncate">{cat.category || 'Other'}</div>
                <div className="flex-1 h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${CATEGORY_COLORS[i]}`} style={{ width: `${(cat.amount / (totalSpend || 1)) * 100}%` }} />
                </div>
                <div className="w-16 text-sm text-right text-slate-600 dark:text-slate-400">${cat.amount.toFixed(0)}</div>
              </div>
            ))
          )}
          <button onClick={() => navigate('/finance?tab=budget_setup')} className="text-xs text-brand hover:underline mt-1">
            Set up budget →
          </button>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
        <button onClick={() => navigate('/finance')} className="w-full py-2 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm">
          View Finance <TrendingUp className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
